import Phaser from 'phaser';
import { TILE_SIZE, type PointEffect, type ShotEffect } from '@game/protocol';

import { effectTextureKey } from '../art/effects.ts';

/**
 * Efeitos visuais de magia e projétil.
 *
 * Puramente cosmético: nada aqui altera estado de jogo. O servidor já resolveu dano,
 * cura e alcance; o que chega é "aconteceu isto, neste tile". É por isso que um efeito
 * perdido por lag não tem consequência — some a animação, não o dano.
 *
 * Cada efeito é um sprite temporário que se anima por tween e se destrói no fim. Sem
 * pool de objetos: efeito acontece em rajada e em quantidade baixa (uma magia por
 * conjuração), e um pool aqui otimizaria o que não é caminho quente.
 */

const CENTER = TILE_SIZE / 2;

/** Duração de um efeito parado. Curto: efeito longo vira poluição na tela. */
const POINT_DURATION_MS = 320;

/** Velocidade do projétil em tiles por segundo. */
const SHOT_SPEED = 14;

export class EffectLayer {
  private readonly scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Explosão, cura, raízes: aparece, cresce um pouco e desaparece. */
  playPoint(effect: PointEffect): void {
    const key = effectTextureKey(effect.effectId);
    if (!this.scene.textures.exists(key)) return;

    const sprite = this.scene.add.sprite(
      effect.x * TILE_SIZE + CENTER,
      effect.y * TILE_SIZE + CENTER,
      key,
    );
    // Acima de qualquer criatura: o efeito precisa ser visto mesmo sobre quem foi
    // atingido, senão a magia parece não ter acontecido em cima do alvo.
    sprite.setDepth(effect.y * 10 + 8);
    sprite.setScale(0.6);

    this.scene.tweens.add({
      targets: sprite,
      scale: 1.25,
      alpha: 0,
      duration: POINT_DURATION_MS,
      ease: 'Quad.easeOut',
      onComplete: () => sprite.destroy(),
    });
  }

  /** Flecha ou bola de fogo viajando entre dois tiles. */
  playShot(shot: ShotEffect): void {
    const key = effectTextureKey(shot.effectId);
    if (!this.scene.textures.exists(key)) return;

    const fromX = shot.fromX * TILE_SIZE + CENTER;
    const fromY = shot.fromY * TILE_SIZE + CENTER;
    const toX = shot.toX * TILE_SIZE + CENTER;
    const toY = shot.toY * TILE_SIZE + CENTER;

    const sprite = this.scene.add.sprite(fromX, fromY, key);
    sprite.setDepth(shot.toY * 10 + 9);
    // O sprite aponta para a direita; girar cobre qualquer ângulo sem um quadro por
    // direção, e o ângulo de um tiro é contínuo.
    sprite.setRotation(Math.atan2(toY - fromY, toX - fromX));

    const tiles = Math.hypot(shot.toX - shot.fromX, shot.toY - shot.fromY);
    // Duração proporcional à distância: um tiro que leva o mesmo tempo para dois tiles
    // e para dez parece teleporte no primeiro caso e lesma no segundo.
    const duration = Math.max(60, (tiles / SHOT_SPEED) * 1000);

    this.scene.tweens.add({
      targets: sprite,
      x: toX,
      y: toY,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });
  }
}
